/* ============================================================================
   rv-racks.js — behaviour for sections/rv-racks-*.liquid.

   Every section renders its markup server-side; this file only wires it up.
   It binds by data attribute, so any number of sections can share a page, and
   it guards itself because each section loads it (a repeated <script> tag would
   otherwise run it twice).

     [data-rvr-typebar]   keeps the current page's link in view on a phone
     [data-rvr-grid]      tabs, Filter & Sort drawer, rack picker, load more
     [data-rvr-matrix]    shows the swipe hint only while the table overflows
     [data-rvr-selector]  Hydra / Manticore / 2.3" tabs
     [data-rvr-archs]     the shape-card carousel dots on a phone
     [data-rvr-goto]      a family card that switches the grid's tab
     [data-open-filters]  any link that should open the filter drawer
     [data-rvr-fitbar]    "Already own a rack?" / "Your rack: …"
     [data-rvr-subtile]   a subcollection tile's "N fit your rack" line
     [data-rvr-subfit]    the subcollection fit table's "Your rack" cell
   ========================================================================= */

(function () {
  'use strict';

  if (window.RvRacks) return;
  window.RvRacks = { version: 1 };

  var $ = function (root, sel) { return root.querySelector(sel); };
  var $$ = function (root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };
  var reduceMotion = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  function money(cents) {
    var v = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '$' + v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function plural(n, one, many) { return n === 1 ? one : many; }

  /* ------------------------------------------------------------------------
     The shopper's rack. A fact about their garage rather than this visit, so
     it is remembered. Shared by every grid on the page.
     --------------------------------------------------------------------- */
  var FIT_STORE = 'bos.racks.fit';
  var FIT = { line: null, shape: null };
  try {
    var saved = JSON.parse(localStorage.getItem(FIT_STORE) || 'null');
    if (saved && saved.line) { FIT.line = saved.line; FIT.shape = saved.shape || null; }
  } catch (e) { /* private mode, or an older value */ }

  function saveFit() {
    try { localStorage.setItem(FIT_STORE, JSON.stringify(FIT)); } catch (e) { /* not worth failing over */ }
  }

  var GRIDS = [];
  // Everything outside a grid that states the rack: fit bars, tile fit lines,
  // the subcollection fit table. Each pushes a repaint.
  var ON_FIT = [];
  function fitChanged() {
    GRIDS.forEach(function (g) { g.refresh(); });
    ON_FIT.forEach(function (fn) { fn(); });
  }

  /* Names for the stored keys, read from the picker's own buttons so the
     wording is the one the merchant set. */
  function pickerAttr(attr, key, want) {
    if (!key) return '';
    var b = document.querySelector('[' + attr + '="' + key + '"][data-label]');
    return b ? (b.getAttribute(want) || '') : key;
  }
  function lineLabel(k) { return pickerAttr('data-line', k, 'data-label'); }
  function lineHw(k) { return pickerAttr('data-line', k, 'data-hw'); }
  function shapeLabel(k) { return pickerAttr('data-shape', k, 'data-label'); }
  function rackKey() { return FIT.line && FIT.shape ? FIT.line + '_' + FIT.shape : null; }

  function esc(t) {
    return String(t == null ? '' : t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ==========================================================================
     TYPE BAR
     ======================================================================= */
  function initTypebar(bar) {
    var wrap = $(bar, '.rvr-typebar__scroll');
    var on = $(bar, '[aria-current="page"]');
    if (!wrap || !on) return;
    var over = on.offsetLeft + on.offsetWidth - wrap.clientWidth;
    if (over > 0) wrap.scrollLeft = over + 24;
  }

  /* ==========================================================================
     GRID
     ======================================================================= */
  function initGrid(root) {
    var lists = {};
    $$(root, '[data-rvr-list]').forEach(function (l) { lists[l.getAttribute('data-rvr-list')] = l; });

    var tabs = $$(root, '[role="tab"][data-bucket]');
    var initial = parseInt(root.getAttribute('data-initial'), 10) || 8;
    var named = root.getAttribute('data-named') === 'true';
    var defaultFit = root.getAttribute('data-default-fit') || '';
    var defaultFitName = root.getAttribute('data-default-fit-name') || '';

    var refine = $(root, '[data-rvr-refine]');
    var panel = $(refine, '.rvr-refine__panel');
    var scrim = $(refine, '.rvr-refine__scrim');
    var opener = $(root, '[data-rvr-open]');
    var badge = $(root, '[data-rvr-badge]');
    var applyBtn = $(root, '[data-rvr-apply]');
    var clearBtn = $(root, '[data-rvr-clear]');
    var rangeEl = $(root, '[data-rvr-range]');
    var skusEl = $(root, '[data-rvr-skus]');
    var noteEl = $(root, '[data-rvr-note]');
    var emptyEl = $(root, '[data-rvr-empty]');
    var moreWrap = $(root, '[data-rvr-more-wrap]');
    var moreBtn = $(root, '[data-rvr-more]');
    var sortSel = $(root, '[data-rvr-sort]');

    var fitEl = $(root, '[data-rvr-fit]');

    var bucket = root.getAttribute('data-open') || 'rack';
    if (!lists[bucket]) bucket = Object.keys(lists).filter(function (k) { return lists[k].children.length; })[0] || 'rack';

    var active = { shape: [], line: [], buy: [], height: [], price: [] };
    var agroups = [];   // attachment sub-collections
    var parts = [];     // component part keys
    var shown = 0;
    // 'collection' (Featured) keeps the order the collection has in admin.
    var sort = root.getAttribute('data-sort') === 'collection' ? 'collection' : 'price-asc';
    // draw() re-appends cards in display order, so stamp each card's place in
    // the collection now, while the DOM still has it.
    Object.keys(lists).forEach(function (k) {
      Array.prototype.forEach.call(lists[k].children, function (c, i) { c.dataset.ord = i; });
    });
    // Closed until the shopper opens it: the accordion's summary line already
    // says which rack is set, and the filters below it lead the drawer.
    var fitOpen = false;

    function cards(bk) {
      return lists[bk] ? Array.prototype.slice.call(lists[bk].children) : [];
    }

    /* ---------------- scope ---------------- */
    function fitKey() {
      if (FIT.line && FIT.shape) return FIT.line + '_' + FIT.shape;
      return defaultFit || null;
    }
    function fitLine() {
      if (FIT.line) return FIT.line;
      return defaultFit ? defaultFit.split('_')[0] : null;
    }
    function labelOf(attr, key) {
      if (!fitEl) return key;
      var b = $(fitEl, '[' + attr + '="' + key + '"]');
      return b ? b.getAttribute('data-label') : key;
    }
    function fitName() {
      if (FIT.line && FIT.shape) {
        return 'a ' + labelOf('data-line', FIT.line) + ' ' + labelOf('data-shape', FIT.shape).toLowerCase();
      }
      return defaultFitName || 'this rack';
    }

    /* ---------------- matching ---------------- */
    var TESTS = {
      shape: function (c, v) { return (c.dataset.shapes || '').indexOf('|' + v + '|') !== -1; },
      line: function (c, v) { return ('|' + (c.dataset.line || '') + '|').indexOf('|' + v + '|') !== -1; },
      buy: function (c, v) { return v === 'build' ? c.dataset.kind === 'build' : c.dataset.kind !== 'build'; },
      height: function (c, v) { return c.dataset.height === v; },
      price: function (c, v) {
        var p = parseInt(c.dataset.price, 10);
        if (!p) return false;
        var b = v.split('-');
        var lo = parseInt(b[0], 10) || 0;
        var hi = b[1] ? parseInt(b[1], 10) : Infinity;
        return p >= lo && p < hi;
      }
    };

    function rackMatches(c, skip) {
      for (var k in active) {
        if (k === skip || !active[k].length) continue;
        if (!active[k].some(function (v) { return TESTS[k](c, v); })) return false;
      }
      return true;
    }

    function addonMatches(c) {
      var picked = c.dataset.bucket === 'component' ? parts : agroups;
      if (!picked.length) return true;
      return picked.some(function (g) {
        return c.dataset.part === g || (c.dataset.groups || '').indexOf(' ' + g + ' ') !== -1;
      });
    }

    function fitsKey(c, key) { return (c.dataset.fitKeys || '').indexOf(' ' + key + ' ') !== -1; }
    function linesOf(c) { return (c.dataset.lines || '').split(' ').filter(Boolean); }

    function componentFits(c, line) {
      if (!line) return true;
      var ls = linesOf(c);
      return !ls.length || ls.indexOf(line) !== -1;
    }

    /* ---------------- ordering ---------------- */
    function priceOf(c) {
      var p = parseInt(c.dataset.price, 10);
      return isNaN(p) || p <= 0 ? Infinity : p;
    }

    function byOrd(list) {
      return list.slice().sort(function (a, b) {
        return (parseInt(a.dataset.ord, 10) || 0) - (parseInt(b.dataset.ord, 10) || 0);
      });
    }

    function orderRacks(list) {
      var groups = {};
      var order = [];
      // Featured: racks in collection order, a pair at its first listing's place.
      if (sort === 'collection') list = byOrd(list);
      list.forEach(function (c) {
        var k = c.dataset.key;
        if (!groups[k]) { groups[k] = []; order.push(k); }
        groups[k].push(c);
      });
      order.forEach(function (k) { groups[k].sort(function (a, b) { return priceOf(a) - priceOf(b); }); });
      if (sort !== 'collection') order.sort(function (a, b) {
        var d = priceOf(groups[a][0]) - priceOf(groups[b][0]);
        if (isNaN(d)) d = 0;
        return sort === 'price-desc' ? -d : d;
      });
      return order.reduce(function (acc, k) { return acc.concat(groups[k]); }, []);
    }

    function byPrice(list) {
      if (sort === 'collection') return byOrd(list);
      return list.slice().sort(function (a, b) {
        var d = priceOf(a) - priceOf(b);
        if (isNaN(d)) d = 0;
        return sort === 'price-desc' ? -d : d;
      });
    }

    /* What the current tab shows, in order. */
    function listFor(bk) {
      var src = cards(bk);
      if (bk === 'rack') return orderRacks(src.filter(function (c) { return rackMatches(c); }));

      src = src.filter(addonMatches);
      if (bk === 'component') {
        var line = fitLine();
        if (!line) return byPrice(src);
        return byPrice(src.filter(function (c) { return linesOf(c).indexOf(line) !== -1; }))
          .concat(byPrice(src.filter(function (c) { return !linesOf(c).length; })));
      }

      var key = fitKey();
      if (!key) return byPrice(src);
      // What fits first, then what we have not tagged. Hiding the untagged would
      // be a lie by omission.
      return byPrice(src.filter(function (c) { return fitsKey(c, key); }))
        .concat(byPrice(src.filter(function (c) { return c.dataset.tagged !== 'true'; })));
    }

    /* ---------------- rack card copy ---------------- */
    function decorateRacks(list) {
      var keys = {};
      list.forEach(function (c) { if (!keys[c.dataset.key]) keys[c.dataset.key] = c; });
      var models = Object.keys(keys).map(function (k) { return keys[k]; });

      function spread(attr) {
        var seen = {};
        models.forEach(function (m) { if (m.dataset[attr]) seen[m.dataset[attr]] = 1; });
        return Object.keys(seen).length;
      }
      var showHeight = spread('hlabel') > 1;
      var showFits = spread('fits') > 1;

      // One comparative line, only where it is true of this rack and not its
      // neighbours.
      var standout = {};
      if (models.length >= 3) {
        var folders = models.filter(function (m) { return m.dataset.folds === 'true'; });
        if (folders.length && folders.length < models.length) {
          folders.forEach(function (m) {
            standout[m.dataset.key] = folders.length === 1 ? 'The one here that folds away' : 'Folds back to the wall';
          });
        }
        var withFits = models.filter(function (m) { return parseInt(m.dataset.fits, 10) > 0; });
        if (withFits.length > 1) {
          var best = Math.max.apply(null, withFits.map(function (m) { return parseInt(m.dataset.fits, 10); }));
          var top = withFits.filter(function (m) { return parseInt(m.dataset.fits, 10) === best; });
          if (top.length === 1 && !standout[top[0].dataset.key]) {
            standout[top[0].dataset.key] = 'Takes the most attachments — ' + best;
          }
        }
        var lows = models.filter(function (m) { return m.dataset.height; }).map(function (m) {
          return { m: m, n: parseFloat(m.dataset.height) };
        }).filter(function (x) { return !isNaN(x.n); });
        if (lows.length > 1) {
          var min = Math.min.apply(null, lows.map(function (x) { return x.n; }));
          var lowest = lows.filter(function (x) { return x.n === min; });
          if (lowest.length === 1 && !standout[lowest[0].m.dataset.key]) {
            standout[lowest[0].m.dataset.key] = 'Fits the lowest ceiling here';
          }
        }
      }

      list.forEach(function (c) {
        var h = $(c, '[data-attr="height"]');
        var f = $(c, '[data-attr="fits"]');
        if (h) h.hidden = !showHeight;
        if (f) f.hidden = !showFits;
        var s = $(c, '[data-rvr-standout]');
        if (s) {
          var t = standout[c.dataset.key] || '';
          s.textContent = t;
          s.hidden = !t;
        }
      });
    }

    /* Four states, styled in rv-racks.css by data-tone. Brand states are ink,
       orange and grey, never green:
         fits   ink        the shopper's rack is listed
         nofit  orange     it is tagged, and not for their rack
         nope   grey       no compatibility listed
         line   outlined   no rack named yet: which lines it is listed for,
                           which is useful and claims nothing about their rack */
    function setBadge(el, text, tone) {
      el.textContent = text;
      el.setAttribute('data-tone', tone);
    }

    function decorateAddons(bk, list) {
      var key = fitKey();
      var told = !!rackKey();
      list.forEach(function (c) {
        var b = $(c, '[data-rvr-fit-badge]');
        if (!b) return;
        if (bk === 'component') {
          var ls = linesOf(c);
          var line = fitLine();
          if (line && ls.length) {
            var name = labelOf('data-line', line);
            if (ls.indexOf(line) !== -1) setBadge(b, '✓ Fits your ' + name, 'fits');
            else setBadge(b, '✕ Doesn’t fit a ' + name, 'nofit');
          } else if (ls.length === 1) {
            setBadge(b, labelOf('data-line', ls[0]) + ' part', 'line');
          } else if (ls.length > 1) {
            setBadge(b, 'Hydra and Manticore', 'line');
          } else {
            setBadge(b, 'Line not listed: check the listing', 'nope');
          }
        } else if (c.dataset.tagged !== 'true') {
          setBadge(b, 'Compatibility not listed', 'nope');
        } else if (key && !fitsKey(c, key)) {
          setBadge(b, '✕ Doesn’t fit' + (told ? ' your rack' : ''), 'nofit');
        } else if (key) {
          setBadge(b, '✓ Fits' + (told ? ' your rack' : ''), 'fits');
        } else {
          var fk = ' ' + (c.dataset.fitKeys || '');
          var hy = fk.indexOf(' hydra_') !== -1;
          var mt = fk.indexOf(' manticore_') !== -1;
          setBadge(b, hy && mt ? 'Listed for Hydra & Manticore'
            : hy ? 'Listed for Hydra only'
            : mt ? 'Listed for Manticore only'
            : 'Compatibility listed', 'line');
        }
      });
    }

    /* ---------------- counts ---------------- */
    function tileCount(bk) {
      if (bk === 'rack') return cards('rack').filter(function (c) { return rackMatches(c); }).length;
      if (bk === 'component') {
        var line = fitLine();
        return cards('component').filter(function (c) { return componentFits(c, line); }).length;
      }
      var key = fitKey();
      return cards('attachment').filter(function (c) {
        return !key || fitsKey(c, key) || c.dataset.tagged !== 'true';
      }).length;
    }

    function nounFor(bk, n, list) {
      var many;
      if (bk === 'rack') {
        var paired = list.some(function (c) { return c.dataset.paired === 'true'; });
        many = paired ? 'listings' : 'racks';
      } else if (bk === 'component') {
        many = named ? 'components' : 'spares';
      } else {
        many = 'attachments';
      }
      return n === 1 ? many.replace(/s$/, '') : many;
    }

    /* ---------------- draw ---------------- */
    function draw() {
      Object.keys(lists).forEach(function (k) {
        lists[k].hidden = k !== bucket;
      });
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-bucket') === bucket;
        t.setAttribute('aria-selected', String(on));
        var n = $(t, '[data-rvr-tab-n]');
        if (n) n.textContent = tileCount(t.getAttribute('data-bucket'));
      });

      var host = lists[bucket];
      if (!host) return;
      var list = listFor(bucket);

      cards(bucket).forEach(function (c) { c.hidden = true; c.removeAttribute('tabindex'); });
      list.forEach(function (c) { host.appendChild(c); });

      if (bucket === 'rack') decorateRacks(list);
      else decorateAddons(bucket, list);

      var full = list.length;
      var n = Math.min(shown || initial, full);
      list.forEach(function (c, i) { c.hidden = i >= n; });
      var more = full - n;

      moreWrap.hidden = more <= 0;
      if (more > 0) moreBtn.textContent = 'Load ' + more + ' more ' + nounFor(bucket, more, list);

      emptyEl.hidden = full > 0;

      var models = {};
      var pairs = 0;
      if (bucket === 'rack') {
        list.forEach(function (c) {
          if (!models[c.dataset.key]) models[c.dataset.key] = 0;
          models[c.dataset.key]++;
        });
        pairs = Object.keys(models).filter(function (k) { return models[k] > 1; }).length;
      }
      var forN = pairs ? ' for ' + Object.keys(models).length + ' racks' : '';

      /* A scoped list is "these fit" then "not listed". The tagged ones that
         don't fit are left out of it, so say how many, or the shopper can't
         tell "we don't sell it" from "it doesn't fit your rack". */
      var tail = '';
      var key = fitKey();
      if (bucket === 'attachment' && key) {
        var confirmed = list.filter(function (c) { return fitsKey(c, key); }).length;
        var dropped = cards('attachment').filter(addonMatches).filter(function (c) {
          return c.dataset.tagged === 'true' && !fitsKey(c, key);
        }).length;
        tail = ' — ' + confirmed + ' confirmed to fit ' + fitName() +
          (full - confirmed ? ', ' + (full - confirmed) + ' not listed' : '') +
          (dropped ? ', ' + dropped + ' that don’t fit hidden' : '');
      }
      var cLine = fitLine();
      if (bucket === 'component' && cLine) {
        var lineN = list.filter(function (c) { return linesOf(c).indexOf(cLine) !== -1; }).length;
        var cDropped = cards('component').filter(addonMatches).length - full;
        tail = ' — ' + lineN + ' fit a ' + labelOf('data-line', cLine) +
          (full - lineN ? ', ' + (full - lineN) + ' line not listed' : '') +
          (cDropped ? ', ' + cDropped + ' that don’t fit hidden' : '');
      }

      if (rangeEl) {
        var noun = nounFor(bucket, full, list);
        rangeEl.textContent = (more > 0
          ? 'Showing ' + n + ' of ' + full + ' ' + noun + forN
          : (full === 1 ? 'Showing the 1 ' + noun : 'Showing all ' + full + ' ' + noun) + forN) + tail;
      }

      if (noteEl) {
        noteEl.textContent = pairs
          ? (sort === 'collection' ? '' : 'Sorted by the cheapest way into each rack. ') + 'The ' + pairs +
            ' racks sold both ways are listed twice, side by side.'
          : '';
        noteEl.hidden = !pairs;
      }
      if (skusEl) skusEl.hidden = bucket !== 'rack';

      paintPanel(list);
      paintFit();
    }

    /* ---------------- panel ---------------- */
    function panelFor(bk) {
      $$(refine, '[data-rvr-for]').forEach(function (g) {
        g.hidden = g.getAttribute('data-rvr-for') !== bk;
      });
    }

    function paintPanel(list) {
      panelFor(bucket);

      var picked;
      var fitCounts = bucket === 'component' ? !!FIT.line : bucket !== 'rack' && !!(FIT.line && FIT.shape);
      if (bucket === 'rack') {
        picked = Object.keys(active).reduce(function (n, k) { return n + active[k].length; }, 0);
      } else {
        picked = (bucket === 'component' ? parts : agroups).length;
      }
      picked += fitCounts ? 1 : 0;

      badge.hidden = !picked;
      badge.textContent = picked;
      clearBtn.hidden = !picked;

      var n = list.length;
      var one = bucket === 'rack' ? 'rack' : bucket;
      applyBtn.innerHTML = 'Show <b class="text-[15px]">' + n + '</b> ' + one + (n === 1 ? '' : 's');

      $$(refine, '.rvr-chip[data-f]').forEach(function (b) {
        var on = active[b.dataset.f].indexOf(b.dataset.v) !== -1;
        b.setAttribute('aria-pressed', String(on));
      });
      var agSet = bucket === 'component' ? parts : agroups;
      $$(refine, '[data-rvr-for="' + bucket + '"] .rvr-ag').forEach(function (b) {
        var g = b.getAttribute('data-ag');
        b.setAttribute('aria-pressed', String(g ? agSet.indexOf(g) !== -1 : !agSet.length));
      });
    }

    /* Don't offer a chip that would empty the grid, or a group with one choice. */
    function pruneChips() {
      var pool = cards('rack');
      $$(refine, '[data-rvr-group]').forEach(function (g) {
        var live = 0;
        $$(g, '.rvr-chip').forEach(function (b) {
          var ok = pool.some(function (c) { return TESTS[b.dataset.f](c, b.dataset.v); });
          b.hidden = !ok;
          if (ok) live++;
        });
        g.hidden = live < 2;
      });
    }

    function countGroups() {
      var att = cards('attachment');
      var wrap = $(refine, '[data-rvr-for="attachment"]');
      if (wrap) {
        var handles = [];
        $$(wrap, '.rvr-ag').forEach(function (b) {
          var g = b.getAttribute('data-ag');
          var n = g ? att.filter(function (c) { return (c.dataset.groups || '').indexOf(' ' + g + ' ') !== -1; }).length : att.length;
          $(b, '[data-n]').textContent = n;
          if (g) { handles.push(g); b.hidden = !n; }
        });
        var loose = att.filter(function (c) {
          return !handles.some(function (g) { return (c.dataset.groups || '').indexOf(' ' + g + ' ') !== -1; });
        }).length;
        var looseEl = $(wrap, '[data-rvr-loose]');
        if (looseEl && handles.length) {
          looseEl.textContent = loose + ' aren’t filed under any of these and show under Everything.';
          looseEl.hidden = !loose;
        }
      }

      var comp = cards('component');
      var box = $(refine, '[data-rvr-parts]');
      if (box) {
        var seen = {};
        comp.forEach(function (c) {
          if (!c.dataset.part) return;
          if (!seen[c.dataset.part]) seen[c.dataset.part] = { label: c.dataset.partLabel, n: 0 };
          seen[c.dataset.part].n++;
        });
        var all = $(box, '.rvr-ag');
        $(all, '[data-n]').textContent = comp.length;
        Object.keys(seen).sort(function (a, b) { return seen[b].n - seen[a].n; }).forEach(function (k) {
          var b = all.cloneNode(true);
          b.setAttribute('data-ag', k);
          b.setAttribute('aria-pressed', 'false');
          b.firstChild.textContent = seen[k].label + ' ';
          $(b, '[data-n]').textContent = seen[k].n;
          box.appendChild(b);
        });
      }
    }

    function openDrawer(on) {
      if (on) { refine.setAttribute('data-open', ''); } else { refine.removeAttribute('data-open'); }
      scrim.hidden = !on;
      opener.setAttribute('aria-expanded', String(on));
      document.documentElement.style.overflow = on ? 'hidden' : '';
      if (on) { var x = $(panel, '[data-rvr-close]'); if (x) x.focus(); } else { opener.focus(); }
    }

    function clearAll() {
      Object.keys(active).forEach(function (k) { active[k] = []; });
      agroups = [];
      parts = [];
      FIT.line = null;
      FIT.shape = null;
      saveFit();
      if (fitEl) fitEl.setAttribute('data-state', 'idle');
      fitOpen = false;
      shown = 0;
      fitChanged();
    }

    /* ---------------- rack picker ---------------- */
    /* The picker leads the panel on every tab, as in the mockup. On the Racks
       tab it narrows nothing on screen; answering it there moves the grid to
       Attachments (see onFitClick), which is what the question is for. */
    function paintFit() {
      if (!fitEl) return;

      var lineOnly = bucket === 'component';
      var state = fitEl.getAttribute('data-state');
      if (FIT.line && state === 'idle') state = FIT.shape ? 'set' : 'line';
      var chosen = state === 'none' ? 'none' : FIT.line;
      var done = state === 'none' || (FIT.line && (lineOnly || FIT.shape));

      $$(fitEl, '[data-line]').forEach(function (b) {
        b.classList.toggle('is-on', !!chosen && b.getAttribute('data-line') === chosen);
      });
      $$(fitEl, '[data-shape]').forEach(function (b) {
        b.classList.toggle('is-on', b.getAttribute('data-shape') === FIT.shape);
      });

      var stepLine = $(fitEl, '[data-rvr-fit-step="line"]');
      var stepShape = $(fitEl, '[data-rvr-fit-step="shape"]');
      stepLine.hidden = lineOnly ? false : (FIT.line && !FIT.shape);
      stepShape.hidden = lineOnly || !FIT.line;

      var body = $(fitEl, '[data-rvr-fit-body]');
      var acc = $(fitEl, '[data-rvr-fit-acc]');
      body.hidden = !fitOpen;
      acc.setAttribute('aria-expanded', String(fitOpen));
      if (done) fitEl.setAttribute('data-answered', ''); else fitEl.removeAttribute('data-answered');

      var sub = $(fitEl, '[data-rvr-fit-sub]');
      if (sub) sub.hidden = !!done;

      var noneBtn = $(fitEl, '[data-line="none"]');
      var v = $(fitEl, '[data-rvr-fit-value]');
      v.textContent = state === 'none' ? (noneBtn ? noneBtn.getAttribute('data-label') : '')
        : (done && lineOnly) ? labelOf('data-line', FIT.line)
        : done ? fitName().replace(/^a /, '')
        : FIT.line ? labelOf('data-line', FIT.line) + ' — pick a shape'
        : 'Any rack';

      var out = $(fitEl, '[data-rvr-fit-out]');
      out.textContent = '';
      if (lineOnly && FIT.line) {
        var n = cards('component').filter(function (c) { return componentFits(c, FIT.line); }).length;
        out.textContent = n + ' ' + plural(n, 'component', 'components') + ' fit a ' + labelOf('data-line', FIT.line) + ' rack.';
      } else if (!lineOnly && FIT.line && FIT.shape) {
        var key = fitKey();
        var att = cards('attachment');
        var fits = att.filter(function (c) { return fitsKey(c, key); }).length;
        var untagged = att.filter(function (c) { return c.dataset.tagged !== 'true'; }).length;
        out.textContent = fits + ' attachments confirmed to fit' +
          (untagged ? ', ' + untagged + ' we haven’t tagged yet' : '') + '.';
      } else if (state === 'none' && noneBtn) {
        var url = noneBtn.getAttribute('data-fallback-url');
        if (url) {
          out.appendChild(document.createTextNode('The ' + noneBtn.getAttribute('data-label') + ' line uses its own attachments — '));
          var a = document.createElement('a');
          a.href = url;
          a.textContent = noneBtn.getAttribute('data-fallback-text') || 'see the range';
          out.appendChild(a);
          out.appendChild(document.createTextNode('.'));
        }
      } else if (FIT.line && !lineOnly) {
        out.textContent = 'Now pick the shape.';
      }
    }

    function onFitClick(b) {
      if (b.hasAttribute('data-line')) {
        var v = b.getAttribute('data-line');
        if (v === 'none') {
          FIT.line = null;
          FIT.shape = null;
          fitEl.setAttribute('data-state', 'none');
          fitOpen = false;
        } else {
          FIT.line = v;
          fitEl.setAttribute('data-state', 'line');
          fitOpen = bucket !== 'component' && !FIT.shape;
        }
      } else {
        FIT.shape = b.getAttribute('data-shape');
        fitEl.setAttribute('data-state', 'set');
        fitOpen = false;
      }
      saveFit();
      shown = 0;
      // A rack named from the Racks tab is a question about what bolts onto it
      // (draw(), via fitChanged, moves the tabs and lists with it)
      if (bucket === 'rack' && FIT.line && FIT.shape && lists.attachment) bucket = 'attachment';
      fitChanged();
    }

    /* ---------------- events ---------------- */
    tabs.forEach(function (t) {
      t.addEventListener('click', function () { select(t.getAttribute('data-bucket')); });
    });

    function select(bk) {
      if (!lists[bk]) return;
      bucket = bk;
      shown = 0;
      draw();
    }

    opener.addEventListener('click', function () { openDrawer(true); });

    refine.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-rvr-close]') || t.closest('[data-rvr-apply]')) { openDrawer(false); return; }
      if (t.closest('[data-rvr-clear]')) { clearAll(); return; }
      if (t.closest('[data-rvr-fit-acc]')) { fitOpen = !fitOpen; paintFit(); return; }

      var fo = t.closest('.rvr-fit__o');
      if (fo) { onFitClick(fo); return; }

      var chip = t.closest('.rvr-chip[data-f]');
      if (chip) {
        var arr = active[chip.dataset.f];
        var i = arr.indexOf(chip.dataset.v);
        if (i === -1) arr.push(chip.dataset.v); else arr.splice(i, 1);
        shown = 0;
        draw();
        return;
      }

      var ag = t.closest('.rvr-ag');
      if (ag) {
        var set = bucket === 'component' ? parts : agroups;
        var g = ag.getAttribute('data-ag');
        if (!g) set.length = 0;
        else {
          var j = set.indexOf(g);
          if (j === -1) set.push(g); else set.splice(j, 1);
        }
        shown = 0;
        draw();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && refine.hasAttribute('data-open')) openDrawer(false);
    });

    var inlineClear = $(root, '[data-rvr-clear-inline]');
    if (inlineClear) inlineClear.addEventListener('click', clearAll);

    /* ---------------- sort menu (a listbox; a native select's list can't be styled) ---------------- */
    if (sortSel) {
      var sortBtn = $(sortSel, '[data-rvr-sort-btn]');
      var sortMenu = $(sortSel, '[data-rvr-sort-menu]');
      var sortOpts = $$(sortMenu, '[role="option"]');
      var cursor = 0;

      function markCursor(i) {
        cursor = (i + sortOpts.length) % sortOpts.length;
        sortOpts.forEach(function (o, n) { o.classList.toggle('is-active', n === cursor); });
      }
      function openSort(on) {
        sortMenu.hidden = !on;
        sortBtn.setAttribute('aria-expanded', String(on));
        if (on) {
          markCursor(sortOpts.findIndex(function (o) { return o.getAttribute('aria-selected') === 'true'; }));
          sortMenu.focus();
        }
      }
      function pickSort(o) {
        sortOpts.forEach(function (x) { x.setAttribute('aria-selected', String(x === o)); });
        $(sortBtn, '[data-rvr-sort-label]').textContent = o.textContent;
        openSort(false);
        sortBtn.focus();
        if (sort !== o.dataset.value) { sort = o.dataset.value; shown = 0; draw(); }
      }

      sortBtn.addEventListener('click', function () { openSort(sortMenu.hidden); });
      sortMenu.addEventListener('click', function (e) {
        var o = e.target.closest('[role="option"]');
        if (o) pickSort(o);
      });
      sortMenu.addEventListener('mousemove', function (e) {
        var o = e.target.closest('[role="option"]');
        if (o) markCursor(sortOpts.indexOf(o));
      });
      sortMenu.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); markCursor(cursor + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); markCursor(cursor - 1); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickSort(sortOpts[cursor]); }
        else if (e.key === 'Escape' || e.key === 'Tab') { openSort(false); if (e.key === 'Escape') sortBtn.focus(); }
      });
      sortBtn.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); openSort(true); }
      });
      document.addEventListener('click', function (e) {
        if (!sortMenu.hidden && !sortSel.contains(e.target)) openSort(false);
      });
    }

    moreBtn.addEventListener('click', function () {
      var was = lists[bucket].querySelectorAll(':scope > :not([hidden])').length;
      shown = Infinity;
      draw();
      var next = lists[bucket].children[was];
      if (next) {
        next.setAttribute('tabindex', '-1');
        next.focus({ preventScroll: true });
        next.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    });

    pruneChips();
    countGroups();

    // ?line=<collection handle> opens the Racks tab with that hardware chip on,
    // so the old hydra-racks / manticore-racks / 2-3-x-2-3-racks links land filtered.
    // Without it, the page's own collection handle does the same: those three
    // collections are assigned this template, so /collections/hydra-racks lands
    // on it directly with no redirect.
    (function () {
      var want;
      try { want = new URLSearchParams(location.search).get('line'); } catch (e) { return; }
      if (!want) want = (location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1];
      if (!want) return;
      want = want.toLowerCase();
      $$(refine, '.rvr-chip[data-f="line"]').forEach(function (b) {
        var slug = b.dataset.v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        if (slug !== want || b.hidden || active.line.indexOf(b.dataset.v) !== -1) return;
        active.line.push(b.dataset.v);
        if (lists.rack) bucket = 'rack';
      });
    })();

    var api = {
      root: root,
      refresh: function () { shown = 0; draw(); },
      select: function (bk) { select(bk); },
      // opts.fit: open with the rack picker expanded ("Set my rack")
      open: function (opts) {
        if (opts && opts.fit && fitEl) { fitOpen = true; paintFit(); }
        openDrawer(true);
      }
    };
    GRIDS.push(api);
    draw();
    return api;
  }

  /* ==========================================================================
     MATRIX
     ======================================================================= */
  function initMatrix(wrap) {
    var scroll = $(wrap, '.rvr-mtx__scroll');
    if (!scroll) return;
    function check() { wrap.classList.toggle('is-scrollable', scroll.scrollWidth > scroll.clientWidth + 4); }
    check();
    window.addEventListener('resize', check, { passive: true });
    // re-measure once layout settles (web fonts, an embedded preview resizing)
    window.addEventListener('load', check);
    if ('ResizeObserver' in window) new ResizeObserver(check).observe(scroll);
  }

  /* ==========================================================================
     SELECTOR
     ======================================================================= */
  function initSelector(root) {
    var segs = $$(root, '[data-rvr-seg]');
    var panels = $$(root, '[data-rvr-seg-panel]');
    segs.forEach(function (b) {
      b.addEventListener('click', function () {
        var k = b.getAttribute('data-rvr-seg');
        segs.forEach(function (o) { o.setAttribute('aria-selected', String(o === b)); });
        panels.forEach(function (p) { p.hidden = p.getAttribute('data-rvr-seg-panel') !== k; });
      });
    });
  }

  /* ==========================================================================
     SHAPE CARDS — scroll-snap does the swiping; this syncs the dots
     ======================================================================= */
  function initArchs(root) {
    var track = $(root, '[data-rvr-archs-track]');
    var dots = $$(root, '[data-rvr-dot]');
    if (!track || !dots.length) return;
    var cardsEls = Array.prototype.slice.call(track.children);
    // the dot and the card in view move together; the card's outline is CSS (phone only)
    function setActive(i) {
      dots.forEach(function (d, n) { d.setAttribute('aria-current', String(n === i)); });
      cardsEls.forEach(function (c, n) { c.classList.toggle('is-active', n === i); });
    }
    setActive(0);
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var best = 0;
        var dist = Infinity;
        cardsEls.forEach(function (c, i) {
          var d = Math.abs(c.offsetLeft - track.offsetLeft - track.scrollLeft);
          if (d < dist) { dist = d; best = i; }
        });
        setActive(best);
        ticking = false;
      });
    }, { passive: true });
    dots.forEach(function (d, i) {
      d.addEventListener('click', function () {
        track.scrollTo({ left: cardsEls[i].offsetLeft - track.offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
        setActive(i);
      });
    });
  }

  /* ==========================================================================
     FIT BAR — the remembered rack, stated once above tiles or a grid
     ======================================================================= */
  function initFitbar(el) {
    var lineOnly = el.getAttribute('data-family') === 'component';
    var noun = el.getAttribute('data-noun') || 'attachment';
    function paint() {
      var set = lineOnly ? !!FIT.line : !!rackKey();
      var name = !set ? ''
        : lineOnly ? lineLabel(FIT.line)
        : lineLabel(FIT.line) + ' ' + shapeLabel(FIT.shape).toLowerCase();
      var hw = set ? lineHw(FIT.line) : '';
      if (set) el.setAttribute('data-set', ''); else el.removeAttribute('data-set');
      el.innerHTML = set
        ? '<span class="rvr-fitbar__k">Your rack</span>' +
          '<b class="rvr-fitbar__v">' + esc(name) + '</b>' +
          (hw ? '<span class="rvr-fitbar__hw">' + esc(hw) + '</span>' : '') +
          '<span class="rvr-fitbar__say">Every card below says whether it’s listed for your rack.</span>' +
          '<a class="rvr-fitbar__go" href="#grid" data-open-filters="rack stay">Change</a>'
        : '<span class="rvr-fitbar__k">Already own a rack?</span>' +
          '<span class="rvr-fitbar__say">Tell us which once. Every card then says whether it’s listed ' +
            'for your rack, here and on every ' + esc(noun) + ' page.</span>' +
          '<a class="rvr-fitbar__go rvr-fitbar__go--solid" href="#grid" data-open-filters="rack stay">Set my rack</a>';
    }
    ON_FIT.push(paint);
    paint();
  }

  /* ==========================================================================
     SUBCOLLECTION TILES — "N fit your rack" under each tile once one is set.
     A tile's data-items holds one record per product: its fit keys
     (attachments) or the lines it is filed to (components).
     ======================================================================= */
  function initSubtile(a) {
    var out = $(a, '[data-rvr-tile-fit]');
    if (!out) return;
    var recs = (a.getAttribute('data-items') || '').split('|').slice(1, -1);
    var comp = a.getAttribute('data-family') === 'component';
    var tileLine = a.getAttribute('data-line') || '';
    var unfiled = a.getAttribute('data-unfiled') === 'true';
    function has(rec, k) { return (' ' + rec + ' ').indexOf(' ' + k + ' ') !== -1; }
    function paint() {
      var t = '';
      if (comp) {
        if (FIT.line && !unfiled) {
          var ln = lineLabel(FIT.line);
          var c = recs.filter(function (r) { return has(r, FIT.line); }).length;
          // the other line's tile only counts the parts filed to both
          if (tileLine && tileLine !== FIT.line) t = c ? c + ' also fit a ' + ln : '';
          else t = c ? c + ' fit your ' + ln : 'None confirmed for your ' + ln;
        }
      } else {
        var k = rackKey();
        if (k) {
          var n = recs.filter(function (r) { return has(r, k); }).length;
          t = n ? n + ' fit your rack' : 'None confirmed for your rack';
        }
      }
      out.textContent = t;
    }
    ON_FIT.push(paint);
    paint();
  }

  /* ==========================================================================
     SUBCOLLECTION FIT TABLE — outline the shopper's own rack
     ======================================================================= */
  function initSubfit(table) {
    function paint() {
      var mine = rackKey();
      $$(table, '[data-key]').forEach(function (td) {
        var on = !!mine && td.getAttribute('data-key') === mine;
        td.classList.toggle('is-mine', on);
        var you = $(td, '.rvr-subfit__you');
        if (you) you.hidden = !on;
      });
    }
    ON_FIT.push(paint);
    paint();
  }

  /* ==========================================================================
     CROSS-SECTION LINKS
     data-open-filters="rack stay": open with the rack picker expanded, and
     don't scroll (the fit bar and the help tile sit above the grid).
     ======================================================================= */
  document.addEventListener('click', function (e) {
    var go = e.target.closest ? e.target.closest('[data-rvr-goto]') : null;
    var of = e.target.closest ? e.target.closest('[data-open-filters]') : null;
    if (!go && !of) return;
    var grid = GRIDS[0];
    if (!grid) return;
    e.preventDefault();
    var how = of ? of.getAttribute('data-open-filters') || '' : '';
    if (go) grid.select(go.getAttribute('data-rvr-goto'));
    if (how.indexOf('stay') === -1) {
      grid.root.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }
    if (of) grid.open({ fit: how.indexOf('rack') !== -1 });
  });

  /* ==========================================================================
     BOOT
     ======================================================================= */
  // Grids first: the fit bars and tiles read their labels from the picker.
  function init(root) {
    $$(root, '[data-rvr-typebar]').forEach(initTypebar);
    $$(root, '[data-rvr-grid]').forEach(initGrid);
    $$(root, '[data-rvr-matrix]').forEach(initMatrix);
    $$(root, '[data-rvr-selector]').forEach(initSelector);
    $$(root, '[data-rvr-archs]').forEach(initArchs);
    $$(root, '[data-rvr-fitbar]').forEach(initFitbar);
    $$(root, '[data-rvr-subtile]').forEach(initSubtile);
    $$(root, '[data-rvr-subfit]').forEach(initSubfit);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);

  // Re-initialise a section the theme editor re-renders.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
